import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import SheepModal from '../components/SheepModal';
import BatchModal from '../components/BatchModal';
import { ageFromDob } from '../utils/format';

const STATUS_FILTERS = ['all', 'active', 'sold', 'deceased'];
const SEX_FILTERS = ['all', 'ewe', 'ram', 'wether'];

const COLUMNS = [
  { key: 'tag_id', label: 'Tag ID' },
  { key: 'breed', label: 'Breed' },
  { key: 'sex', label: 'Sex' },
  { key: 'date_of_birth', label: 'Age' },
  { key: 'status', label: 'Status' },
  { key: 'dam_tag', label: 'Dam' },
  { key: 'litter_size', label: 'Litter' },
];

export default function FlockList({ onNavigate }) {
  const [sheep, setSheep] = useState([]);
  const [filter, setFilter] = useState('active');
  const [sexFilter, setSexFilter] = useState('all');
  const [breedFilter, setBreedFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'tag_id', dir: 'asc' });
  const [selected, setSelected] = useState(() => new Set());
  const [batchMode, setBatchMode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = () => {
    const s = filter === 'all' ? undefined : filter;
    setError('');
    api.listSheep(s).then(setSheep).catch(err => setError(err.message));
  };

  useEffect(() => { load(); setSelected(new Set()); }, [filter]);

  const breeds = useMemo(
    () => Array.from(new Set(sheep.map(s => s.breed).filter(Boolean))).sort(),
    [sheep]
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const rows = sheep.filter(s =>
      (sexFilter === 'all' || s.sex === sexFilter) &&
      (breedFilter === 'all' || s.breed === breedFilter) &&
      (!normalizedSearch ||
        s.tag_id.toLowerCase().includes(normalizedSearch) ||
        (s.breed || '').toLowerCase().includes(normalizedSearch))
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      if (sort.key === 'litter_size') { av = av || 0; bv = bv || 0; return (av - bv) * dir; }
      // date_of_birth sorts chronologically (older birth date first when asc).
      av = (av ?? '').toString().toLowerCase();
      bv = (bv ?? '').toString().toLowerCase();
      return av.localeCompare(bv) * dir;
    });
  }, [sheep, sexFilter, breedFilter, normalizedSearch, sort]);

  const toggleSort = (key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every(s => selected.has(s.id));
  const toggleAll = () => {
    setSelected(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        filtered.forEach(s => next.delete(s.id));
        return next;
      }
      return new Set([...prev, ...filtered.map(s => s.id)]);
    });
  };

  const selectedSheep = filtered.filter(s => selected.has(s.id));

  const handleSaved = () => { setShowModal(false); load(); };
  const handleBatchDone = (msg) => {
    setBatchMode(null);
    setSelected(new Set());
    setMessage(msg);
    load();
  };

  const sortIndicator = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const onRowKey = (e, id) => {
    if (e.key === 'Enter') onNavigate('profile', id);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <span className="eyebrow">Animal records</span>
          <h1 className="page-title">Your flock</h1>
          <p className="page-subtitle">Find animals, update records, and complete routine work in one place.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><span className="button-plus">+</span> Add animal</button>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}
      {message && <div className="alert alert-success mb-4">{message}</div>}

      <div className="filter-shell">
        <div className="status-filters" aria-label="Filter by status">
        {STATUS_FILTERS.map(s => (
          <button
            key={s}
            className={filter === s ? 'active' : ''}
            onClick={() => setFilter(s)}
            style={{ textTransform: 'capitalize' }}
          >{s}<span>{s === filter ? sheep.length : ''}</span></button>
        ))}
        </div>
        <div className="filter-bar">
        <select className="filter-select" value={sexFilter} onChange={e => setSexFilter(e.target.value)} aria-label="Filter by sex">
          {SEX_FILTERS.map(s => <option key={s} value={s}>{s === 'all' ? 'All sexes' : `${s.charAt(0).toUpperCase()}${s.slice(1)}s`}</option>)}
        </select>
        <select className="filter-select" value={breedFilter} onChange={e => setBreedFilter(e.target.value)} aria-label="Filter by breed">
          <option value="all">All breeds</option>
          {breeds.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <input
          className="search-input"
          placeholder="Search sheep ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="filter-count">{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="batch-bar">
          <span><strong>{selected.size}</strong> selected</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setBatchMode('weight')}>⚖️ Record Weight</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setBatchMode('vaccination')}>💉 Record Vaccination</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⌕</div>
            <h3>No matching animals</h3>
            <p>Try changing your search or filters.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                  </th>
                  {COLUMNS.map(col => (
                    <th key={col.key} className="sortable" onClick={() => toggleSort(col.key)}>
                      {col.label}{sortIndicator(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr
                    key={s.id}
                    className={selected.has(s.id) ? 'row-selected' : ''}
                    tabIndex={0}
                    onKeyDown={e => onRowKey(e, s.id)}
                    onClick={() => onNavigate('profile', s.id)}
                  >
                    <td className="checkbox-col" data-label="Select" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} aria-label={`Select ${s.tag_id}`} />
                    </td>
                    <td data-label="Tag ID"><strong>{s.tag_id}</strong></td>
                    <td data-label="Breed">{s.breed || '—'}</td>
                    <td data-label="Sex"><span className={`badge badge-${s.sex}`}>{s.sex}</span></td>
                    <td data-label="Age">{s.date_of_birth ? ageFromDob(s.date_of_birth) : '—'}</td>
                    <td data-label="Status"><span className={`badge badge-${s.status}`}>{s.status}</span></td>
                    <td data-label="Dam">{s.dam_tag || '—'}</td>
                    <td data-label="Litter">{s.litter_size || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && <SheepModal onClose={() => setShowModal(false)} onSaved={handleSaved} />}
      {batchMode && (
        <BatchModal
          mode={batchMode}
          sheep={selectedSheep}
          onClose={() => setBatchMode(null)}
          onDone={handleBatchDone}
        />
      )}
    </div>
  );
}
